import React, { memo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  fetchHomepage,
  fetchSubscriptions,
  saveHomepage,
  saveSubscriptions,
  toggleSubscription,
  toggleAllSubscriptions,
  HOMEPAGE_SET,
} from '../actions/preferences';
import WalletSectionCard from '../components/profile/WalletSectionCard';
import WalletDefaultHomepage from '../components/profile/preferences/WalletDefaultHomepage';
import WalletEmailSubscriptions from '../components/profile/preferences/WalletEmailSubscriptions';
import style from '../style/components/profile/preferences/WalletPreferences.module.scss';

const WalletPreferences = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Use the store directly inside callbacks so handlers can read current
  // `preferences.isFetching` and `preferences.subscriptions` without those
  // values appearing in the useCallback deps array. `store` and `dispatch`
  // are stable for the app's lifetime, so the callbacks below remain
  // referentially equal across re-renders — child memo()s stay effective.
  const store = useStore();
  // Select each field separately. useSelector compares each returned value
  // with `===`, so the container only re-renders when one of these specific
  // values changes — not on every `state.preferences` mutation (e.g. shared
  // `isFetching` toggles during a sibling save).
  const homepage = useSelector((state) => state.preferences.homepage);
  const homepageBusy = useSelector((state) => state.preferences.homepageBusy);
  const subscriptions = useSelector((state) => state.preferences.subscriptions);
  const subscriptionsBusy = useSelector(
    (state) => state.preferences.subscriptionsBusy,
  );

  useEffect(() => {
    dispatch(fetchHomepage());
    dispatch(fetchSubscriptions());
  }, [dispatch]);

  const handleHomepageChange = useCallback(
    (value) => {
      if (store.getState().preferences.isFetching) return;
      // Optimistic update — flip the radio immediately so the UI doesn't
      // wait for the PUT round-trip. saveHomepage's catch block re-fetches
      // on failure, which restores the prior value.
      dispatch({ type: HOMEPAGE_SET, payload: value });
      dispatch(saveHomepage(value));
    },
    [dispatch, store],
  );

  const handleToggleSubscription = useCallback(
    (index) => {
      const { subscriptions: subs, isFetching: fetching } =
        store.getState().preferences;
      if (fetching) return;
      const nextSubscriptions = [
        ...subs.slice(0, index),
        { ...subs[index], active: !subs[index].active },
        ...subs.slice(index + 1),
      ];
      dispatch(saveSubscriptions(nextSubscriptions));
      dispatch(toggleSubscription(index));
    },
    [dispatch, store],
  );

  const handleToggleAllSubscriptions = useCallback(() => {
    const { subscriptions: subs, isFetching: fetching } =
      store.getState().preferences;
    if (fetching) return;
    const allActive = subs.every((sub) => sub.active);
    const nextSubscriptions = subs.map((sub) => ({
      ...sub,
      active: !allActive,
    }));
    dispatch(saveSubscriptions(nextSubscriptions));
    dispatch(toggleAllSubscriptions());
  }, [dispatch, store]);

  return (
    <WalletSectionCard
      title={t('walletPreferences.title')}
      icon={<idme-icon name="settings" color="stone" size="large" />}
    >
      <WalletDefaultHomepage
        value={homepage}
        disabled={homepageBusy}
        onChange={handleHomepageChange}
      />
      <div className={style.separator} />
      <WalletEmailSubscriptions
        subscriptions={subscriptions}
        // subscriptionsBusy drives this prop (not the shared isFetching) so
        // the card only disables during subscription saves, not homepage ones.
        isFetching={subscriptionsBusy}
        onToggleSubscription={handleToggleSubscription}
        onToggleAll={handleToggleAllSubscriptions}
      />
    </WalletSectionCard>
  );
};

export default memo(WalletPreferences);
